import { Fragment, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

import { Dialog, Transition } from '@headlessui/react';

import { OptimizedImage } from 'shared/components/ui/optimized-image/optimized-image';

import CloseIcon from 'assets/png/close-icon-blue.png?format=webp;avif&as=picture';

import { AdditionalSettingsModalContent } from './additional-settings-modal-content';

interface AdditionalSettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const AdditionalSettingsModalMobilePortal = ({
  isOpen,
  onClose,
}: AdditionalSettingsModalProps) => {
  const { t } = useTranslation();

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  return createPortal(
    <Transition
      show={isOpen}
      enter="transition-opacity duration-300"
      enterFrom="opacity-0"
      enterTo="opacity-100"
      leave="transition-opacity duration-300"
      leaveFrom="opacity-100"
      leaveTo="opacity-0"
      as={Fragment}
    >
      <Dialog as="div" className="fixed inset-0 z-50 flex flex-col justify-end" onClose={onClose}>
        <Transition.Child as={Fragment}>
          <Dialog.Overlay className="fixed inset-0 bg-black/60" />
        </Transition.Child>

        <Transition.Child
          as={Fragment}
          enter="transform transition ease-out duration-300"
          enterFrom="translate-y-full"
          enterTo="translate-y-0"
          leave="transform transition ease-in duration-200"
          leaveFrom="translate-y-0"
          leaveTo="translate-y-full"
        >
          <div className="relative mx-auto flex max-h-[80vh] w-full max-w-md flex-col rounded-t-2xl bg-[#080540] px-[16px]">
            <div className="m-2 flex justify-center lg:m-4">
              <div className="h-1 w-10 rounded-full bg-[#5555aa]" />
            </div>

            <Dialog.Title className="mb-4 flex items-center px-4 text-base font-semibold lg:text-xl">
              <p className="gradient-title flex-1 text-center">
                {t('modals.additionalSettings.additionalSettingsTitle')}
              </p>
              <button
                onClick={onClose}
                aria-label="Close modal"
                data-testid="close-settings-button-mobile"
              >
                <OptimizedImage
                  imageData={CloseIcon}
                  alt="Close"
                  className="h-6 w-6 transition hover:scale-110 lg:h-8 lg:w-8"
                />
              </button>
            </Dialog.Title>

            <AdditionalSettingsModalContent onClose={onClose} isMobile />
          </div>
        </Transition.Child>
      </Dialog>
    </Transition>,
    document.body,
  );
};
